import {
    BuildingStorefrontIcon,
    HomeIcon,
    UsersIcon,
    WrenchScrewdriverIcon,
} from '@heroicons/react/24/outline';
import { PlatoLogo } from '@plato/ui-legacy/assets/icons/plato-logo';
import { DialogProvider } from '@plato/ui-legacy/components/dialog/dialog-provider';
import { LoadingBlockFullScreen } from '@plato/ui-legacy/components/loading/loading-block-full-screen';
import { Button } from '@plato/ui-legacy/components/ui/button';
import { Drawer, DrawerContent, DrawerIconClose, DrawerTitle } from '@plato/ui/containers/drawer';
import { useBreakpoint } from '@plato/ui/util/use-breakpoint';
import * as Sentry from '@sentry/react';
import { TRPCClientError } from '@trpc/client';
import clsx from 'clsx';
import { ChevronsRightIcon } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useSignOut } from '../lib/hooks/use-sign-out.ts';
import { api } from '../utils/api.ts';
import { getFullName } from '../utils/name.ts';

// MARK - clean this up
type IconType = typeof HomeIcon;

type NavigationItem = {
    name: string;
    href: string;
    icon: IconType;
    mustBeExact?: boolean;
};
const navigation: NavigationItem[] = [
    { name: 'Dashboard', href: '/admin', icon: HomeIcon, mustBeExact: true },
    { name: 'Customers', href: '/admin/customers', icon: BuildingStorefrontIcon },
    { name: 'Admin Users', href: '/admin/admin-users', icon: UsersIcon },
    { name: 'Tools', href: '/admin/tools', icon: WrenchScrewdriverIcon },
];

export function SuperAdminLayout() {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const isDesktop = useBreakpoint('lg');
    const location = useLocation();
    const navigate = useNavigate();
    const { signOutFn } = useSignOut();

    const {
        data: superAdmin,
        error,
        isLoading,
    } = api.superAdmin.me.getMe.useQuery(undefined, {
        staleTime: 5 * 60 * 1000,
    });

    useEffect(() => {
        if (!error || !(error instanceof TRPCClientError)) {
            return;
        }
        const data: unknown = error.data;
        if (typeof data !== 'object' || data === null || !('code' in data)) {
            return;
        }
        if (data.code === 'FORBIDDEN') {
            void navigate('/', { replace: true });
        } else if (data.code === 'NOT_FOUND') {
            signOutFn();
        }
    }, [error, navigate, signOutFn]);

    useEffect(() => {
        if (!superAdmin || process.env.NODE_ENV === 'development') {
            return;
        }
        Sentry.setUser({
            id: superAdmin.id,
            email: superAdmin.email,
            name: getFullName(superAdmin.firstName, superAdmin.lastName),
            role: 'SUPER_ADMIN',
        });
        Sentry.setTag('admin', true);
    }, [superAdmin]);

    const isCurrent = (item: NavigationItem) => {
        const pathname = location.pathname + location.search + location.hash;
        if (!pathname) {
            return false;
        }
        const href = item.href;
        if (!href) {
            return false;
        }

        if (item.mustBeExact) {
            return href === pathname;
        } else {
            return pathname.startsWith(href);
        }
    };

    if (isLoading || !superAdmin) {
        return <LoadingBlockFullScreen />;
    }

    return (
        <DialogProvider>
            <div>
                <Drawer open={sidebarOpen && !isDesktop} onOpenChange={setSidebarOpen}>
                    <DrawerContent
                        side="left"
                        // Legacy admin chrome: a narrow blue nav panel, not a DS container surface.
                        className="max-w-xs bg-blue-600 sm:w-full sm:max-w-xs"
                    >
                        <DrawerTitle className="sr-only">Navigation</DrawerTitle>
                        <div className="flex grow flex-col gap-y-5 overflow-y-auto px-6 pb-4">
                            <div className="flex h-16 shrink-0 items-center justify-between">
                                <PlatoLogo className="h-8" variant="light" />
                                <DrawerIconClose
                                    label="Close sidebar"
                                    className="bg-transparent text-white hover:bg-white/10"
                                />
                            </div>
                            <nav className="flex flex-1 flex-col">
                                <ul className="flex flex-1 flex-col gap-y-7">
                                    <li>
                                        <ul className="-mx-2 space-y-1">
                                            {navigation.map((item) => (
                                                <li key={item.name}>
                                                    <Link
                                                        to={item.href}
                                                        className={clsx(
                                                            isCurrent(item)
                                                                ? 'bg-blue-700 text-white'
                                                                : 'text-indigo-200 hover:bg-blue-700 hover:text-white',
                                                            'group flex gap-x-3 rounded-md p-2 text-base font-semibold leading-6',
                                                        )}
                                                    >
                                                        <item.icon
                                                            className={clsx(
                                                                isCurrent(item)
                                                                    ? 'text-white'
                                                                    : 'text-indigo-200 group-hover:text-white',
                                                                'h-6 w-6 shrink-0',
                                                            )}
                                                            aria-hidden="true"
                                                        />
                                                        {item.name}
                                                    </Link>
                                                </li>
                                            ))}
                                        </ul>
                                    </li>
                                </ul>
                            </nav>
                        </div>
                    </DrawerContent>
                </Drawer>

                {/* Static sidebar for desktop */}
                <div className="hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:w-72 lg:flex-col">
                    {/* Sidebar component, swap this element with another sidebar if you like */}
                    <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-blue-600 px-6 pb-4">
                        <div className="flex h-16 shrink-0 items-center">
                            <PlatoLogo className="h-8" variant="light" />
                            <div className="ml-3 font-medium text-white">Plato Admin Portal</div>
                        </div>
                        <nav className="flex flex-1 flex-col">
                            <ul className="flex flex-1 flex-col gap-y-7">
                                <li>
                                    <ul className="-mx-2 space-y-1">
                                        {navigation.map((item) => (
                                            <li key={item.name}>
                                                <Link
                                                    to={item.href}
                                                    className={clsx(
                                                        isCurrent(item)
                                                            ? 'bg-blue-700 text-white'
                                                            : 'text-indigo-200 hover:bg-blue-700 hover:text-white',
                                                        'group flex gap-x-3 rounded-md p-2 text-base font-semibold leading-6',
                                                    )}
                                                >
                                                    <item.icon
                                                        className={clsx(
                                                            isCurrent(item)
                                                                ? 'text-white'
                                                                : 'text-indigo-200 group-hover:text-white',
                                                            'h-6 w-6 shrink-0',
                                                        )}
                                                        aria-hidden="true"
                                                    />
                                                    {item.name}
                                                </Link>
                                            </li>
                                        ))}
                                    </ul>
                                </li>
                            </ul>
                        </nav>
                        <Button onClick={signOutFn}>Sign Out</Button>
                    </div>
                </div>
                <div className="flex lg:pl-72 h-dvh">
                    <div className="top-0 bottom-0 bg-blue-600 p-2 py-4 lg:hidden">
                        <Button
                            variant="outline"
                            size="icon-s"
                            icon={ChevronsRightIcon}
                            onClick={() => {
                                setSidebarOpen(true);
                            }}
                        />
                    </div>
                    <main className="w-full h-full overflow-auto max-h-dvh">
                        <Outlet />
                    </main>
                </div>
            </div>
        </DialogProvider>
    );
}
